import { systemStore, type SystemConfig } from '@/store/system'

const normalizeHex = (value: string, fallback: string) => {
  const v = `${value || ''}`.trim()
  const withHash = v.startsWith('#') ? v : `#${v}`
  return /^#[0-9A-Fa-f]{6}$/.test(withHash) ? withHash : fallback
}

const hexToRgb = (hex: string) => {
  const raw = hex.replace('#', '')
  const int = parseInt(raw, 16)
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 }
}

const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`

const luminance = (hex: string) => {
  const { r, g, b } = hexToRgb(hex)
  const a = [r, g, b].map((v) => {
    const x = v / 255
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2]
}

const mixHex = (a: string, b: string, weight = 0.5) => {
  const c1 = hexToRgb(a)
  const c2 = hexToRgb(b)
  return rgbToHex(
    c1.r * (1 - weight) + c2.r * weight,
    c1.g * (1 - weight) + c2.g * weight,
    c1.b * (1 - weight) + c2.b * weight
  )
}

export const applySystemTheme = (config: SystemConfig) => {
  const primary = normalizeHex(config.colors.primary, '#0f4c81')
  const root = document.documentElement

  root.style.setProperty('--system-navbar', primary)
  const navbarText = luminance(primary) > 0.35 ? '#201f1e' : '#ffffff'
  root.style.setProperty('--system-navbar-text', navbarText)

  const primLum = luminance(primary)
  let sidebarBg: string
  let sidebarText: string
  let sidebarActive: string
  let sidebarHover: string

  if (primLum < 0.35) {
    sidebarBg = mixHex(primary, '#000000', 0.38)
    sidebarText = 'rgba(255, 255, 255, 0.88)'
    sidebarActive = 'rgba(255, 255, 255, 0.14)'
    sidebarHover = 'rgba(255, 255, 255, 0.07)'
  } else {
    sidebarBg = mixHex(primary, '#ffffff', 0.88)
    sidebarText = '#201f1e'
    sidebarActive = 'rgba(0, 0, 0, 0.1)'
    sidebarHover = 'rgba(0, 0, 0, 0.05)'
  }

  root.style.setProperty('--system-sidebar', sidebarBg)
  root.style.setProperty('--system-sidebar-text', sidebarText)
  root.style.setProperty('--system-sidebar-muted',
    primLum < 0.35 ? 'rgba(255, 255, 255, 0.52)' : 'rgba(0, 0, 0, 0.45)')
  root.style.setProperty('--system-sidebar-active', sidebarActive)
  root.style.setProperty('--system-sidebar-hover', sidebarHover)

  root.style.setProperty('--system-search-bg',
    luminance(primary) < 0.35 ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.1)')
  root.style.setProperty('--system-search-border',
    luminance(primary) < 0.35 ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.2)')
}

export const useSystemTheme = () => ({
  async loadAndApply() {
    const config = await systemStore.refresh()
    applySystemTheme(config)
    return config
  }
})
